import { Bar } from "react-chartjs-2";
import { registerCharts } from "./register-chart";

registerCharts();

export interface StackedBarDataset {
    label: string;
    data: number[];
    backgroundColor: string;
}

export interface StackedBarChartProps {
    labels: string[];
    datasets: StackedBarDataset[];
    ariaLabel: string;
    className?: string;
    /** When omitted, the y-axis scales to the stacked peak with headroom. */
    yAxisMax?: number;
}

function resolveYAxisMax(datasets: StackedBarDataset[], yAxisMax?: number): { max: number; stepSize: number } {
    if (yAxisMax != null) {
        return { max: yAxisMax, stepSize: yAxisMax / 5 };
    }

    const pointCount = datasets[0]?.data.length ?? 0;
    let peak = 0;

    for (let index = 0; index < pointCount; index += 1) {
        const stackTotal = datasets.reduce((sum, dataset) => sum + (dataset.data[index] ?? 0), 0);
        peak = Math.max(peak, stackTotal);
    }

    if (peak === 0) {
        return { max: 100, stepSize: 20 };
    }

    const paddedPeak = Math.ceil(peak * 1.1);
    const stepSize = Math.max(50, Math.ceil(paddedPeak / 5 / 50) * 50);

    return { max: stepSize * 5, stepSize };
}

/**
 * Reusable stacked bar chart for time-series status breakdowns.
 */
export function StackedBarChart({ labels, datasets, ariaLabel, className, yAxisMax }: StackedBarChartProps) {
    const { max, stepSize } = resolveYAxisMax(datasets, yAxisMax);

    const data = {
        labels,
        datasets: datasets.map((dataset) => ({
            ...dataset,
            borderWidth: 0,
            borderRadius: 2,
            maxBarThickness: 28,
        })),
    };

    return (
        <div className={className} role="img" aria-label={ariaLabel}>
            <Bar
                data={data}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        x: {
                            stacked: true,
                            grid: { display: false },
                            ticks: { color: "#6B7280", font: { size: 11 } },
                        },
                        y: {
                            stacked: true,
                            beginAtZero: true,
                            max,
                            grid: { color: "#E5E7EB" },
                            ticks: {
                                color: "#6B7280",
                                font: { size: 11 },
                                stepSize,
                            },
                        },
                    },
                    plugins: {
                        legend: {
                            position: "bottom",
                            align: "start",
                            labels: {
                                boxWidth: 10,
                                boxHeight: 10,
                                padding: 20,
                                color: "#374151",
                                font: { size: 12 },
                            },
                        },
                        tooltip: {
                            mode: "index",
                            intersect: false,
                            backgroundColor: "#FFFFFF",
                            borderColor: "#E5E7EB",
                            borderWidth: 1,
                            titleColor: "#111827",
                            bodyColor: "#374151",
                            footerColor: "#374151",
                            padding: 12,
                            boxPadding: 6,
                            usePointStyle: true,
                            callbacks: {
                                label: (context) => {
                                    const label = context.dataset.label ?? "";
                                    const value = typeof context.parsed.y === "number" ? context.parsed.y : 0;
                                    return `${label}: ${value}`;
                                },
                            },
                        },
                    },
                }}
            />
        </div>
    );
}
